<template>
    <Modal ref="modalRef" max-width="2xl" @cancel="showCancelConfirmation">
        <template #title>
            <div class="-mx-8 -my-4 bg-[#002377] px-8 py-4 text-white">
                <asset-short-info :asset="asset" :clickable="false">
                    <template #top>
                        <status-badge :enable-mobile="true" :operability="asset.status.operability" />
                    </template>
                </asset-short-info>
                <div v-if="store.showMaintenanceTypeToggle" class="mt-8 flex justify-center">
                    <animated-toggle-buttons
                        :model-value="store.selectedMaintenanceType"
                        :options="maintenanceTypeOptions.data"
                        class="w-100"
                        @update:model-value="store.selectMaintenanceType($event)"
                    />
                </div>
            </div>
        </template>
        <div v-if="store.currentState === 'Pending'" class="flex h-40 items-center justify-center">
            <spinner />
        </div>
        <template v-else-if="showOperabilityConfirmation">
            <c-alert v-if="store.failureCodesError && !statusChangeToOperable" class="mb-3" color="danger">
                <div class="d-flex align-items-start gap-3">
                    <icon class="mt-0.5 shrink-0" icon="cil-warning" />
                    <div>
                        <strong>{{ trans('fieldresolvableissue.issue_triage.failure_codes_error_title') }}</strong>
                        <p class="mt-1 mb-0">{{ store.failureCodesError }}</p>
                    </div>
                </div>
            </c-alert>
            <div
                v-if="statusChangeToOperable"
                v-html="
                    trans('assets.modal.operability_change.description', {
                        operability: operabilityLabel,
                    })
                "
            />

            <row class="mt-3">
                <column>
                    <jet-textarea
                        v-model="form.additionalInformation"
                        :horizontal="false"
                        :invalidFeedback="form.errors.additionalInformation"
                        :label="trans('assets.modal.operability_change.additional_information.label')"
                        :maxlength="80"
                        :placeholder="trans('assets.modal.operability_change.additional_information.placeholder')"
                        :rows="4"
                        name="additional_information"
                    />
                </column>
            </row>
        </template>
        <template v-else>
            <issue-triage-section :form="form" />
        </template>
        <template #footer>
            <template v-if="store.currentState === 'Pending'">
                <!-- loading — no footer actions -->
                <div />
            </template>
            <template v-else-if="showOperabilityConfirmation">
                <div class="m-0 flex w-100 flex-row items-center justify-center gap-3">
                    <jet-button :disabled="form.processing" style="flex: 1" type="button" variant="secondary" @click.stop="showCancelConfirmation">
                        {{ trans('general.action.cancel') }}
                    </jet-button>
                    <jet-button
                        :disabled="form.processing"
                        :is-processing="form.processing"
                        data-testid="modal-primary-button"
                        style="flex: 1"
                        variant="primary"
                        @click.stop="confirmOperabilityChange"
                    >
                        {{ trans('general.action.confirm') }}
                    </jet-button>
                </div>
            </template>
            <template v-else-if="store.currentState === 'CorrectiveMaintenanceWithIssueTriaging'">
                <div class="relative flex w-100 items-center justify-center gap-2">
                    <jet-button class="!absolute start-0 min-w-0 !px-0" type="button" variant="link" @click="showCancelConfirmation">{{
                        trans('general.action.cancel')
                    }}</jet-button>
                    <jet-button
                        :disabled="isTriageProcessing"
                        :is-processing="isTriageProcessing"
                        variant="secondary"
                        @click.stop="skipAndAssignToSupervisor()"
                    >
                        {{ trans('fieldresolvableissue.diagnose_modal.skip_and_assign') }}
                    </jet-button>
                </div>
            </template>
            <template v-else-if="store.currentState === 'CorrectiveMaintenanceWithKnownIssue' && store.guidance">
                <div class="m-0 flex w-100 flex-row items-center justify-center gap-3">
                    <jet-button
                        :disabled="isTriageProcessing || form.processing"
                        style="flex: 1"
                        type="button"
                        variant="secondary"
                        @click.stop="reportFailedTroubleshooting(store.workOrderId)"
                    >
                        {{ trans('fieldresolvableissue.diagnose_modal.no_assign') }}
                    </jet-button>
                    <jet-button
                        :disabled="isTriageProcessing || !store.workOrderId"
                        :is-processing="isTriageProcessing"
                        data-testid="modal-primary-button"
                        style="flex: 1"
                        variant="primary"
                        @click.stop="resolveIssue"
                    >
                        {{ trans('fieldresolvableissue.diagnose_modal.yes_resolved') }}
                    </jet-button>
                </div>
            </template>
            <template v-else>
                <jet-button
                    :disabled="isTriageProcessing || form.processing"
                    :is-processing="isTriageProcessing"
                    data-testid="modal-primary-button"
                    variant="primary"
                    @click.stop="handleUnknownOrNotListedIssueSubmission()"
                >
                    {{ trans('fieldresolvableissue.diagnose_modal.assign_to_supervisor') }}
                </jet-button>
            </template>
        </template>
    </Modal>

    <confirm-modal
        :cancel-text="trans('fieldresolvableissue.diagnose_modal.confirmation.cancel_text')"
        :confirm-text="trans('fieldresolvableissue.diagnose_modal.confirmation.continue_text')"
        :title="trans('fieldresolvableissue.diagnose_modal.confirmation.title')"
        name="confirm-cancel"
        @cancel="onCancel"
    >
        <ul class="list-disc space-y-2">
            <li>{{ trans('fieldresolvableissue.diagnose_modal.confirmation.message_unsaved') }}</li>
            <li>{{ trans('fieldresolvableissue.diagnose_modal.confirmation.message_operable') }}</li>
            <li>{{ trans('fieldresolvableissue.diagnose_modal.confirmation.message_no_downtime') }}</li>
        </ul>
    </confirm-modal>
</template>

<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { router, useForm, usePage } from '@inertiajs/vue3';
import { loadWorkOrderDetails } from '@/Pages/WorkOrders/loadWorkOrderDetails';
import AssetShortInfo from '@/Components/AssetShortInfo.vue';
import StatusBadge from '@/Components/StatusBadge.vue';
import AnimatedToggleButtons from '@/Components/AnimatedToggleButtons.vue';
import JetButton from '@/Jetstream/Button.vue';
import { trans } from '@/mixins/i18n.js';
import JetTextarea from '@/Jetstream/Textarea.vue';
import { visitModal } from '@inertiaui/modal-vue';
import Column from '@/Components/Column.vue';
import { useDiagnoseFieldIssueStore } from '@/stores/diagnoseFieldIssueStore';
import IssueTriageSection from './DiagnoseFieldIssue/IssueTriageSection.vue';
import ConfirmModal from '@/Components/ConfirmModal.vue';
import { eventBus, events } from '@/eventBus.js';
import { route } from 'ziggy-js';
import { $http } from '@/bootstrap.js';

defineOptions({ inheritAttrs: false });

const props = defineProps({
    asset: {
        type: Object,
        required: false,
    },

    maintenanceTypeOptions: Object,
    processSteps: Object,
    currentProcessStep: Object,
    guidance: Object,
    assetOperabilityChangedAt: String,
});

const modalRef = ref(null);
const currentLocale = usePage().props.currentLocale ?? 'en';
const isTriageProcessing = ref(false);
const store = useDiagnoseFieldIssueStore();
const form = useForm({
    operability: props.asset.status.operability,
    additionalInformation: '',
    reason: 'corrective_maintenance_required',
    attachments: [],
});

store.setup({
    assetId: props.asset.id,
    assetOperability: props.asset.status.operability,
    assetOperabilityChangedAt: props.assetOperabilityChangedAt,
    guidance: props.guidance,
    processSteps: props.processSteps,
});

const showOperabilityConfirmation = computed(
    () =>
        store.failureCodesError ||
        store.currentState === 'RestoringOperability' ||
        store.currentState === 'PreventativeMaintenance' ||
        store.currentState === 'CorrectiveMaintenanceWithoutIssueTriaging',
);

const operabilityLabel = computed(() => {
    const text = trans('assets.modal.operability_change.label.operable');
    return `<span><em class="operability-indicator bg-success operable"></em>&nbsp;<b>${text}</b></span>`;
});

const statusChangeToOperable = computed(() => {
    return props.asset.status.operability === 'operable';
});

async function resolveIssue() {
    if (isTriageProcessing.value || !store.workOrderId) return;
    isTriageProcessing.value = true;

    try {
        store.setLoadingStep('resolving');

        const response = await $http.post(route('assets.operability-triage.resolve', props.asset.id), {
            workorder_id: store.workOrderId,
            failure_code_id: store.selectedFailureCode.id,
        });

        modalRef.value?.close();

        await loadWorkOrderDetails(response.data.workOrderId, {
            navigate: route('assets.show', props.asset.id),
        });

        eventBus.$emit(events.assetOperabilityUpdated);
    } finally {
        isTriageProcessing.value = false;
        store.setLoadingStep(null);
    }
}

async function skipAndAssignToSupervisor(errorCodeId = null, workOrderId = null) {
    if (isTriageProcessing.value) return;
    isTriageProcessing.value = true;

    try {
        workOrderId = workOrderId || (await store.initiate({ id: errorCodeId })).workOrderId;
        modalRef.value?.close();
        await loadWorkOrderDetails(workOrderId);
    } finally {
        isTriageProcessing.value = false;
    }
}

async function reportFailedTroubleshooting(workOrderId) {
    if (!workOrderId) return;

    if (isTriageProcessing.value) return;
    isTriageProcessing.value = true;

    try {
        store.setLoadingStep('resolving');
        await $http.post(route('assets.operability-triage.record', props.asset.id), {
            workorder_id: store.workOrderId,
            failure_code_id: store.selectedFailureCode.id,
            event: 'troubleshooting_failed',
        });

        modalRef.value?.close();

        await loadWorkOrderDetails(workOrderId);
    } finally {
        isTriageProcessing.value = false;
        store.setLoadingStep(null);
    }
}

function confirmOperabilityChange() {
    const changedAt = new Date().toISOString();
    const state = store.currentState;

    form.transform((data) => {
        if (state === 'CorrectiveMaintenanceWithKnownIssue' && store.selectedFailureCode) {
            return {
                operability: data.operability,
                reason: 'corrective_maintenance_required',
                failure_code_id: store.selectedFailureCode.id,
                changed_at: changedAt,
            };
        }
        return {
            operability: data.operability,
            reason: data.operability === 'inoperable' ? store.selectedMaintenanceType : undefined,
            additionalInformation: data.additionalInformation || null,
            changed_at: changedAt,
        };
    }).post(route('assets.confirm-operability-change', props.asset.id), {
        preserveState: true,
        onSuccess: () => {
            eventBus.$emit(events.updateNotifications);
            modalRef.value?.close();
        },
    });
}

function handleUnknownOrNotListedIssueSubmission() {
    if (!store.workOrderId) return;

    if (isTriageProcessing.value) return;
    isTriageProcessing.value = true;

    store.setLoadingStep('resolving');

    form.transform((data) => {
        return {
            additionalInformation: data.additionalInformation,
            attachments: data.attachments,
        };
    }).patch(route('assets.operability-triage.update-triage-details', store.workOrderId), {
        preserveState: true,
        onSuccess: () => {
            modalRef.value?.close();
            loadWorkOrderDetails(store.workOrderId);
        },
        onFinish: () => {
            isTriageProcessing.value = false;
            store.setLoadingStep(null);
        },
    });
}

function onCancel() {
    modalRef.value?.close();
    router.visit(route('assets.show', { asset: store.assetId }));
}

function showCancelConfirmation() {
    if (store.selectedFailureCode !== null) {
        return;
    }

    if (store.assetOperability === 'operable') {
        onCancel();
        return;
    }

    visitModal('#confirm-cancel');
}

onUnmounted(() => store.reset());
</script>
